"use client";

import React, { useEffect } from "react";
import createKeyCrawler from "./create-crawler";

/**
 * A utility that will fire a provided callback when a specified key sequence has been pressed. Does not render any elements.
 * @param {object} props - The component props
 * @param {string[]} props.sequence - Order of keys pressed to watch for. To see what keys are available check out: [MDN docs on keys](https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values)
 * @param {Function} props.onMatch - Callback to execute when sequence of keys is pressed
 * @param {Function} [props.keyCompareFn] - Configure comparison logic of pressed keys
 * @param {string[]} [props.ignore] - Keys to ignore
 * @returns {React.Fragment} An empty fragment
 */
import { jsx as _jsx } from "react/jsx-runtime";
const HotKey = ({
  ignore = [],
  sequence,
  keyCompareFn = (x, y) => x === y,
  onMatch
}) => {
  useHotKey(sequence, onMatch, {
    ignore,
    keyCompareFn
  });

  // we return a fragment so that react-docgen will recognize this as a component
  return /*#__PURE__*/_jsx(React.Fragment, {});
};
export default HotKey;
export const useHotKey = (sequence, onMatch, options = {}) => {
  const {
    keyCompareFn = (x, y) => x === y,
    ignore = []
  } = options;
  useEffect(() => {
    let keyCrawler = null;
    const setCrawler = () => {
      keyCrawler = createKeyCrawler([].concat(sequence), keyCompareFn);
    };
    const listen = e => {
      const {
        key
      } = e;
      const keyIgnore = [].concat(ignore);
      if (!~keyIgnore.indexOf(key) && keyCrawler(key)) {
        onMatch();
      }
    };
    setCrawler();
    window.addEventListener("keydown", listen);
    return () => {
      window.removeEventListener("keydown", listen);
    };
  }, [ignore, keyCompareFn, onMatch, sequence]);
};